import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { areChartAnimationsDisabled } from "@/utils/chartAnimation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { CarbonChartData } from "@/types/results";
import { formatNumber } from "@/utils/numberFormatter";

interface CarbonPieChartProps {
  data: CarbonChartData[];
  title: string;
  className?: string;
}

export const CarbonPieChart = ({
  data,
  title,
  className,
}: CarbonPieChartProps) => {
  const { t } = useLanguage();

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border rounded-md shadow-sm">
          <p className="text-sm font-medium mb-1">{data.name}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-600">
                {t("results.carbon.units.kgCO2e")}:
              </span>
              <span className="text-sm font-medium">
                {formatNumber(data.value)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-600">
                {t("results.carbon.units.percentage")}:
              </span>
              <span className="text-sm font-medium">
                {data.percentage < 0.1 ? '<0.1%' : `${data.percentage.toFixed(1)}%`}
              </span>
            </div>
            {data.breakdown && (
              <div className="mt-2 pt-2 border-t">
                <p className="text-xs text-gray-500 mb-1">Breakdown:</p>
                {data.breakdown.materials && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-600">
                      {t("results.carbon.phases.materials")}:
                    </span>
                    <span className="text-xs">
                      {formatNumber(data.breakdown.materials)} kg CO₂e
                    </span>
                  </div>
                )}
                {data.breakdown.ecr && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-600">
                      {t("results.carbon.phases.energyAndResidue")}:
                    </span>
                    <span className="text-xs">
                      {formatNumber(data.breakdown.ecr)} kg CO₂e
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="flex flex-wrap justify-center gap-4 mt-4">
        {payload.map((entry: any, index: number) => (
          <div key={`legend-${index}`} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-gray-600">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                outerRadius={120}
                innerRadius={40}
                paddingAngle={2}
                dataKey="value"
                isAnimationActive={!areChartAnimationsDisabled()}
                animationDuration={1000}
                minAngle={1}
                label={false}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
